<div class="transcript-panel" [class.transcript-panel--style-panel-open]="showSubtitleStylePanel">
  <!-- Tab switcher -->
  <div class="transcript-panel__tabs">
    <button
      type="button"
      class="transcript-panel__tab"
      [class.transcript-panel__tab--active]="activeTab === 'subtitles'"
      (click)="setActiveTab('subtitles')"
    >
      Subtitles
    </button>
    <button
      type="button"
      class="transcript-panel__tab"
      [class.transcript-panel__tab--active]="activeTab === 'transcript'"
      (click)="setActiveTab('transcript')"
    >
      AI Transcript
    </button>
  </div>

  <!-- Transcript sub-tab -->
  @if (activeTab === 'transcript') {
    <div class="transcript-panel__body">
      @if (state()?.loadingTranscriptTracks) {
        <p class="transcript-panel__loading">Loading transcript tracks…</p>
      }
      @if (state()?.transcriptLoadError && !state()?.loadingTranscriptTracks) {
        <p class="transcript-panel__error">{{ state()?.transcriptLoadError }}</p>
      }
      @if (!state()?.loadingTranscriptTracks) {
      <!-- Source Audio (TAGS-style block) -->
      <div class="transcript-panel__tag-block">
        <label class="filter-option-header">SOURCE AUDIO:</label>
        <div class="transcript-panel__source-card transcript-panel__source-card--selected">
          <span class="transcript-panel__source-label">{{ state()?.sourceAudioLanguage || 'Source' }}</span>
          <button
            type="button"
            class="transcript-panel__regenerate-btn"
            [disabled]="state()?.isRegeneratingTranscript"
            (click)="onRegenerate()"
          >
            @if (state()?.isRegeneratingTranscript) {
              <span class="transcript-panel__regenerate-spinner"></span>
              Regenerating...
            } @else {
              Regenerate
            }
          </button>
        </div>
      </div>

      <!-- Transcript Language (TAGS-style block) -->
      <div class="transcript-panel__tag-block">
        <label class="filter-option-header">TRANSCRIPT LANGUAGE:</label>
        <ul class="transcript-panel__tag-list">
          @for (track of (state()?.transcriptTracks ?? []); track track.language) {
            <li>
              <button
                type="button"
                class="transcript-panel__pill"
                [class.transcript-panel__pill--active]="state()?.activeTranscriptLanguage === track.language"
                (click)="setActiveTranscriptLanguage(track.language)"
              >
                {{ track.language }}
              </button>
            </li>
          }
        </ul>
      </div>

      <!-- Search -->
      <div class="transcript-panel__search-wrap">
        <ichannel-input
          [placeholder]="'Search transcript...'"
          [value]="transcriptSearch"
          (valueChange)="transcriptSearch = $event"
        ></ichannel-input>
      </div>

      <!-- Transcript blocks list (read-only; Go to IN/Out only) -->
      <div class="transcript-panel__blocks" #transcriptContainerRef>
        @for (block of filteredTranscriptBlocks; track block.id) {
          <evrideo-clipping-studio-transcript-block
            [block]="block"
            (goToIn)="onGoToIn($event)"
            (goToOut)="onGoToOut($event)"
          ></evrideo-clipping-studio-transcript-block>
        }
        @if (filteredTranscriptBlocks.length === 0) {
          <p class="transcript-panel__empty">No transcript blocks</p>
        }
      </div>
      }
    </div>
  }

  <!-- Subtitles sub-tab -->
  @if (activeTab === 'subtitles') {
    <div class="transcript-panel__body">
      <!-- Primary + Secondary actions grid (same as React) -->
      <div class="transcript-panel__actions-grid">
        <button type="button" class="transcript-panel__btn transcript-panel__btn--outline" (click)="generateFromTranscript()">
          Generate from Transcript
        </button>
        <button type="button" class="transcript-panel__btn transcript-panel__btn--outline" (click)="addSubtitle()">
          Add Subtitle
        </button>
        <label class="transcript-panel__file-label">
          <input type="file" accept=".srt,.vtt" (change)="onImportFile($event)" class="transcript-panel__file-input" />
          Import SRT/VTT
        </label>
        <div class="transcript-panel__export-wrap">
          <button
            type="button"
            class="transcript-panel__btn transcript-panel__btn--secondary"
            (click)="showExportDropdown ? closeExportDropdown() : openExportDropdown()"
          >
            Export SRT/VTT
          </button>
          @if (showExportDropdown) {
            <div class="transcript-panel__export-dropdown">
              <button type="button" class="transcript-panel__export-option" (click)="exportSubs('srt')">
                Export SRT
              </button>
              <button type="button" class="transcript-panel__export-option" (click)="exportSubs('vtt')">
                Export VTT
              </button>
            </div>
          }
        </div>
      </div>

      <!-- Language Management (table layout for fixed columns/rows) -->
      <div class="transcript-panel__lang-mgmt">
        <div class="transcript-panel__lang-mgmt-header">
          <span class="transcript-panel__lang-mgmt-title">Language Management</span>
        </div>
        @if (state()?.loadingSubtitleTracks) {
          <p class="transcript-panel__loading">Loading subtitle tracks…</p>
        } @else if (state()?.subtitleLoadError) {
          <p class="transcript-panel__error">{{ state()?.subtitleLoadError }}</p>
        } @else {
        <div class="transcript-panel__lang-mgmt-table-wrap">
          <table class="transcript-panel__lang-table">
            <tbody>
              @for (row of subtitleLanguageRows; track row.languageName) {
                <tr class="transcript-panel__lang-row" [class.transcript-panel__lang-row--active]="row.languageName === state()?.activeSubtitleLanguage">
                  <td class="transcript-panel__lang-cell transcript-panel__lang-cell--info">
                    <span class="transcript-panel__lang-row-name">{{ row.displayName }}</span>
                    <span class="transcript-panel__lang-row-sublabel">{{ row.subtitleLabel }}</span>
                  </td>
                  <td class="transcript-panel__lang-cell transcript-panel__lang-cell--btn">
                    <div class="transcript-panel__lang-actions">
                      <div class="transcript-panel__lang-trash-slot">
                        @if (row.isImported) {
                          <button
                            type="button"
                            class="transcript-panel__lang-btn transcript-panel__lang-btn--trash"
                            (click)="onDeleteImportedTrack(row.languageName)"
                            aria-label="Delete imported subtitles"
                          >
                            <span
                              class="transcript-panel__lang-trash-icon"
                              [inlineSVG]="'/assets/icons/pop-trash.svg'"
                              aria-hidden="true"
                            ></span>
                          </button>
                        }
                      </div>
                      @if (row.languageName === state()?.activeSubtitleLanguage) {
                        <button type="button" class="transcript-panel__lang-btn transcript-panel__lang-btn--active">ACTIVE</button>
                      } @else if (row.hasSubs) {
                        <button type="button" class="transcript-panel__lang-btn transcript-panel__lang-btn--switch" (click)="onSwitchSubtitleLanguage(row.languageName)">SWITCH</button>
                      } @else {
                        <button type="button" class="transcript-panel__lang-btn transcript-panel__lang-btn--generate" (click)="onGenerateSubsForLanguage(row.languageName)">GENERATE</button>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        }
      </div>

      <!-- Style block: label row + Edit + Search row -->
      <div class="transcript-panel__style-block">
        <div class="transcript-panel__style-label-row">
          <span class="transcript-panel__lang-mgmt-title">Style</span>
        </div>
        <div class="transcript-panel__toolbar">
          <button type="button" class="transcript-panel__btn transcript-panel__btn--outline transcript-panel__btn--style" (click)="openStylePanel()">
            Edit
          </button>
          <div class="transcript-panel__search-wrap">
            <ichannel-input
              [placeholder]="'Search subs...'"
              [value]="subtitleSearch"
              (valueChange)="subtitleSearch = $event"
            ></ichannel-input>
          </div>
        </div>
      </div>

      <!-- Subtitle blocks list -->
      <div class="transcript-panel__blocks" (click)="clearSubtitleSelection()">
        @for (sub of filteredSubtitles; track sub.id) {
          <div #subtitleBlockContainer [attr.data-subtitle-id]="sub.id">
            <evrideo-clipping-studio-subtitle-block
              [subtitle]="sub"
              [selected]="isSubtitleSelected(sub)"
              (select)="onSubtitleBlockSelect($event)"
              (textChange)="onSubtitleTextChange($event)"
              (split)="onSplitSubtitle($event)"
              (delete)="onDeleteSubtitle($event)"
            ></evrideo-clipping-studio-subtitle-block>
          </div>
        }
        @if (filteredSubtitles.length === 0) {
          <p class="transcript-panel__empty">No subtitles</p>
        }
      </div>
    </div>
  }

  <!-- Floating toolbar hidden: transcript is read-only; editing is only on subtitles -->

  <!-- Subtitle style panel (slide-out) -->
  @if (showSubtitleStylePanel) {
    <evrideo-clipping-studio-subtitle-style-panel
      [initialStyle]="state()?.subtitleStyle ?? null"
      (close)="closeStylePanel()"
      (applyStyle)="onApplySubtitleStyle($event)"
    ></evrideo-clipping-studio-subtitle-style-panel>
  }
</div>
